<template>
  <div
    class="lifecycle-chart-root"
    v-if="portfolioHistories && portfolioHistories.length > 1"
  >
    <div class="chart-header">
      <horizontal-title-tooltip-section
        :title="`Portfolio ${portfolio.portfolio_name} - ${$t(
          'portfolioManager.portfolioLifecycle.versionHistory'
        )}`"
        :tooltip="$t('portfolioManager.portfolioLifecycle.versionHistoryHint')"
        size="m"
        spacing="left"
      />
      <div class="header-right">
        <div class="switch-root">
          <span>
            {{ $t("portfolioManager.portfolioLifecycle.inTime") }}
          </span>
          <block-switch v-model="isSpatial">{{
            $t("portfolioManager.portfolioLifecycle.spatially")
          }}</block-switch>
        </div>
        <b-button
          variant="primary"
          @click="
            downloadPortfolioExcelHistory([
              { id: portfolio.portfolio_id, name: portfolio.portfolio_name },
            ])
          "
        >
          <download-icon :size="20" />
          {{ $t("portfolioManager.portfolioLifecycle.exportHistory") }}
        </b-button>
      </div>
    </div>
    <div class="chart-with-sides">
      <div class="chart-left">
        <horizontal-title-tooltip-section
          v-if="isSpatial"
          :title="$t('portfolioManager.significantMeasures.expectedReturn')"
          :tooltip="$t('portfolioManager.hints.expectedReturn')"
        />
        <span class="axisLabel body-medium" v-else>
          {{ $t("portfolioManager.portfolioLifecycle.percentages") }}
        </span>
      </div>
      <time-versions-chart
        v-if="!isSpatial"
        :data="portfolioHistories"
        @chartClick="handleChartClick"
      />
      <spatial-versions-chart
        v-else
        :data="portfolioHistories"
        @chartClick="handleChartClick"
      />
      <div class="chart-right">
        <div class="legend">
          <div class="legend-items">
            <legend-item
              v-for="(legendItem, i) in legendItems"
              v-bind="legendItem"
              v-bind:key="i"
              isSquare
            />
          </div>
        </div>
      </div>
    </div>
    <div class="chart-footer">
      <horizontal-title-tooltip-section
        v-if="isSpatial"
        :title="$t('portfolioManager.significantMeasures.volatility')"
        :tooltip="$t('portfolioManager.hints.volatility')"
      />
      <span v-else class="axisLabel body-medium">{{
        $t("portfolioManager.portfolioLifecycle.date")
      }}</span>
    </div>
  </div>
</template>

<script>
import {
  DARK_GREEN,
  DARK_RED,
  LIFECYCLE_ICONS,
  LIGHT_GREEN,
  LIGHT_RED,
  PRIMARY_FONT_BLUE,
} from "../../utils/constants";
import { mapActions, mapGetters } from "vuex";
import BlockSwitch from "../inputs/BlockSwitch";
import DownloadIcon from "vue-material-design-icons/DownloadOutline.vue";
import HorizontalTitleTooltipSection from "../inputs/HorizontalTitleTooltipSection.vue";
import LegendItem from "../LegendItem.vue";
import SpatialVersionsChart from "./SpatialVersionsChart.vue";
import TimeVersionsChart from "./TimeVersionsChart.vue";

export default {
  name: "portfolio-lifecycle-chart",
  components: {
    HorizontalTitleTooltipSection,
    LegendItem,
    BlockSwitch,
    DownloadIcon,
    TimeVersionsChart,
    SpatialVersionsChart,
  },
  data() {
    return {
      isSpatial: false,
      legends: {
        spatial: [
          {
            name: this.$i18n.t(
              "portfolioManager.portfolioLifecycle.initialPortfolio"
            ),
            symbol: LIFECYCLE_ICONS.initialPortfolio,
          },
          {
            name: this.$i18n.t(
              "portfolioManager.portfolioLifecycle.weeklyChanges"
            ),
            symbol: LIFECYCLE_ICONS.weeklyChange,
          },
          {
            name: this.$i18n.t(
              "portfolioManager.portfolioLifecycle.rebalancedPortfolio"
            ),
            symbol: LIFECYCLE_ICONS.rebalancedPortfolio,
          },
        ],
        inTime: [
          {
            name: this.$i18n.t(
              "portfolioManager.significantMeasures.expectedReturn"
            ),
            dotColor: LIGHT_GREEN,
            textColor: DARK_GREEN,
          },
          {
            name: this.$i18n.t(
              "portfolioManager.significantMeasures.volatility"
            ),
            dotColor: LIGHT_RED,
            textColor: DARK_RED,
          },
          {
            name: this.$i18n.t(
              "portfolioManager.portfolioLifecycle.rebalancedPortfolioStar"
            ),
            textColor: PRIMARY_FONT_BLUE,
          },
        ],
      },
    };
  },
  methods: {
    ...mapActions("portfolioManagerStore", [
      "getPortfolioHistories",
      "downloadPortfolioExcelHistory",
      "getHistoryVersion",
    ]),
    handleChartClick(id) {
      this.getHistoryVersion(id);
    },
  },
  computed: {
    ...mapGetters("portfolioManagerStore", ["portfolioHistories", "portfolio"]),
    legendItems() {
      return this.isSpatial ? this.legends.spatial : this.legends.inTime;
    },
  },
  mounted() {
    if (this.portfolio) {
      this.getPortfolioHistories(this.portfolio.portfolio_id);
    }
  },
  watch: {
    "portfolio.portfolio_id"() {
      this.getPortfolioHistories(this.portfolio.portfolio_id);
    },
  },
};
</script>

<style lang="scss" scoped>
.lifecycle-chart-root {
  padding: toRem(15);
  border: 1px solid #f3f3f3;
  @include dps-box;
  position: relative;

  .axisLabel {
    color: black;
  }
  .chart-header {
    display: flex;
    justify-content: space-between;
    .header-right {
      display: flex;
      button {
        margin-left: toRem(30);
      }
    }
    .title-container {
      width: auto;
    }
  }
  .switch-root {
    display: flex;
    align-items: center;
    .block-switch-root {
      border: none;
      padding-left: toRem(10);
      margin-bottom: 0;
    }
  }
  .lifecycle-chart {
    width: 80% !important;
  }
  .chart-footer {
    display: flex;
    justify-content: center;
    .flex {
      width: 70px;
    }
    .axisLabel {
      margin-top: toRem(35);
    }
  }
  .chart-with-sides {
    display: flex;
    .chart-left {
      display: flex;
      align-items: center;
      max-width: 50px;
      .title-container{
       margin-top: 6vw;
      }
      .flex,
      .axisLabel {
        transform: rotate(-90deg);
      }
    }
  }
  .legend {
    min-width: 150px;
  }
  .chart-header {
    display: flex;
    flex-wrap: wrap;
  }
  .chart-right {
    display: flex;
    align-items: center;
    .frontier-expl {
      display: flex;
      align-items: center;
      .markline {
        width: 15px;
        height: 3px;
        background-color: #002333;
        margin-right: 10px;
        &.repl {
          background-color: #779eff;
        }
      }
    }
  }
}
</style>